import { useState } from 'react';
import { ArrowRight, LogIn, TriangleAlert } from 'lucide-react';
import { useAuth } from '../auth';
import { Button } from '../components/ui';
import { Logo } from '../App';
import { tx } from '../i18n';

/**
 * The sentence for a refused sign-in (`?error=` from routes/auth.ts). Only the server's
 * codes are shown: the page used to print whatever text the link carried, inside its
 * red box, so a crafted link could put any message on the real sign-in page.
 */
export function refusalText(code: string | null): string | null {
  switch (code) {
    case 'not-staff':
      return tx('Sheet Studio is for the tutors and assistants of the course. Your CMS account is not one of them.');
    case 'no-role':
      return tx('The CMS did not say which role your account has, so Sheet Studio cannot let you in. Please tell the course assistants.');
    case 'deactivated':
      return tx('Your Sheet Studio account has been deactivated. Please ask the course assistants.');
    case 'expired':
      return tx('This sign-in took too long or was not started here. Please try again.');
    case 'sso-failed':
      return tx('The sign-in with the CMS did not work. Please try again.');
  }
  return null;
}

export function LoginPage() {
  const { signInWithSso, debugLogin, methods, bootstrap, loading, reachable } = useAuth();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // a rejected CMS round trip lands back here with its reason, a code, in the query
  const ssoError = refusalText(new URLSearchParams(window.location.search).get('error'));

  const submitDebug = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await debugLogin(name.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : tx('Something went wrong'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full w-full">
      <div className="hidden flex-1 flex-col justify-between bg-[var(--color-brand-600)] p-10 text-white lg:flex">
        <div className="flex items-center gap-3">
          <Logo size={34} />
          <span className="text-lg font-semibold">Sheet Studio</span>
        </div>
        <div className="max-w-md space-y-5">
          <h1 className="text-3xl font-semibold leading-tight">
            {tx('Every Prog 1 exercise since 2016, ready to reuse.')}
          </h1>
          <p className="text-brand-100">
            {tx('Browse the whole pool with renders and sources, tag by topic and difficulty, then drag exercises onto a new sheet and build it as PDF, LaTeX or Typst — together with your co-tutors, live.')}
          </p>
          <ul className="space-y-2 text-sm text-brand-100">
            {[
              tx('Faceted search across ten years of tutorial and exercise sheets'),
              tx('Drag & drop sheet composition with live PDF preview'),
              tx('Collaborative editing with presence'),
              tx('Export as PDF, .tex, .typ or one zip with everything'),
            ].map((line) => (
              <li key={line} className="flex items-start gap-2">
                <ArrowRight size={16} className="mt-0.5 shrink-0 text-camel-500" />
                {line}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-xs text-brand-200">{tx('Saarland University · Programming 1')}</p>
      </div>

      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-5">
          <div className="lg:hidden">
            <Logo size={34} />
          </div>
          <div>
            <h2 className="text-xl font-semibold">{tx('Sign in')}</h2>
            <p className="mt-1 text-sm text-[var(--ink-soft)]">
              {/* only the development debug login still makes the first account admin */}
              {bootstrap && methods?.debug && !methods?.sso
                ? tx('No accounts exist yet — the first person to sign in administers this instance.')
                : tx('For the tutors and assistants of the course. Sign in with your Saarland University account.')}
            </p>
          </div>

          {ssoError && (
            <p className="rounded-lg border border-[color-mix(in_srgb,var(--color-danger-500)_35%,transparent)] bg-[color-mix(in_srgb,var(--color-danger-500)_8%,transparent)] px-3 py-2 text-sm text-[var(--color-danger-500)]">
              {ssoError}
            </p>
          )}

          {methods.sso ? (
            <Button
              variant="primary"
              className="w-full"
              onClick={() => signInWithSso()}
              disabled={loading}
            >
              <LogIn size={16} />
              {tx('Continue with Saarland University')}
            </Button>
          ) : (
            !loading && (
              <p className="rounded-lg border border-[var(--line)] bg-[var(--surface-sunken)] px-3 py-2 text-sm text-[var(--ink-soft)]">
                {/*
                  A server that could not be answered at all is not a server without SSO.
                  Reporting the second for the first sends the reader hunting for a
                  configuration problem that does not exist.
                */}
                {!reachable ? (
                  <>
                    {tx('The server could not be reached, so the available sign-in methods are unknown. Check that it is running, then reload.')}
                  </>
                ) : (
                  <>
                    {tx('Single sign-on is not configured on this server.')}
                    {methods.debug
                      ? tx(' Use the development sign-in below.')
                      : tx(' Ask an administrator to set STUDIO_SSO_ENDPOINT and STUDIO_SSO_SECRET.')}
                  </>
                )}
              </p>
            )
          )}

          {methods.debug && (
            <form
              onSubmit={submitDebug}
              className="space-y-3 rounded-xl border border-dashed border-[color-mix(in_srgb,var(--color-camel-500)_50%,var(--line))] bg-[color-mix(in_srgb,var(--color-camel-500)_6%,transparent)] p-4"
            >
              <div className="flex items-start gap-2 text-sm font-medium text-[var(--ink)]">
                <TriangleAlert size={16} className="mt-0.5 shrink-0 text-[var(--color-camel-500)]" />
                {tx('Development sign-in')}
              </div>
              <p className="text-xs text-[var(--ink-soft)]">
                {tx('Debug mode is on, so any name signs you in without a password. Turn it off with')}{' '}
                <code className="font-mono">STUDIO_AUTH_DEBUG=0</code>
                {/* German goes on with a comma, which takes no space before it */}
                {/^[,.;:]/.test(tx('before this server is reachable by anyone else.')) ? '' : ' '}
                {tx('before this server is reachable by anyone else.')}
              </p>
              <label className="block">
                <span className="label">{tx('Name')}</span>
                <input
                  className="input mt-1"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Dieter Schlau"
                  autoComplete="off"
                  required
                />
              </label>
              {error && (
                <p className="text-sm text-[var(--color-danger-500)]">{error}</p>
              )}
              <Button type="submit" className="w-full" loading={busy} disabled={!name.trim()}>
                {tx('Sign in as this person')}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
